import api from './client';

// A funds-health probe that merely completes the HTTP round trip — including
// a backend-legitimate `200 OK` body of `null` when funds are unavailable —
// must never be treated as a successful, usable funds result. This is the
// single shared boundary every current brokerApi.getFunds() caller (Header's
// funds-health probe, FundsWidget, BrokerTokenGateModal's confirmation
// fetch) goes through, so a missing/null/malformed payload becomes a
// rejected result everywhere instead of requiring each caller to re-check
// it. Applies identically to every broker (FYERS, Zerodha, AngelOne, Kotak
// Neo, Nuvama, MasterTrust, Alice Blue) — none of them get a free pass.
//
// A legitimate zero-balance account (available_cash = used_margin =
// total_balance = 0) must remain healthy, so this checks field presence and
// numeric type/finiteness, never truthiness of the values themselves.
export function isUsableBrokerFunds(funds) {
  if (!funds || typeof funds !== 'object' || Array.isArray(funds)) return false;
  return ['available_cash', 'used_margin', 'total_balance'].every(
    (key) => typeof funds[key] === 'number' && Number.isFinite(funds[key])
  );
}

async function getFunds() {
  const funds = await api.get('/broker/funds');
  if (!isUsableBrokerFunds(funds)) {
    throw new Error('broker_funds_unavailable');
  }
  return funds;
}

export const brokerApi = {
  getSupported:      ()        => api.get('/broker/supported'),
  getCurrent:        ()        => api.get('/broker/current'),
  getAll:            ()        => api.get('/broker/all'),
  map:               (payload) => api.post('/broker/map', payload),
  updateCredentials: (payload) => api.patch('/broker/credentials', payload),
  unmap:             (name)    => api.delete(`/broker/unmap/${name}`),
  activate:          (name)    => api.post(`/broker/activate/${name}`),
  testConnection:    (payload) => api.post('/broker/test-connection', payload),
  getOAuthUrl:       (name)    => api.get('/broker/oauth/url', { params: { broker_name: name } }),
  getTokenStatus:    ()        => api.get('/broker/token-status'),
  getFunds,
  getProfile:        ()        => api.get('/broker/profile'),
  getOrderbook:      ()        => api.get('/broker/orderbook'),
  getPaperOrderbook: ()        => api.get('/broker/paper-orderbook'),
  getPositions:      ()        => api.get('/broker/positions'),
  getPaperPositions: (params)  => api.get('/broker/paper-positions', params ? { params } : undefined),
  getHoldings:       ()        => api.get('/broker/holdings'),
  getOrderLog:       (params)  => api.get('/broker/order-log', { params }),
  getProxyBinding:   (brokerName) => api.get('/broker/proxy-binding', brokerName ? { params: { broker_name: brokerName } } : undefined),
  reserveProxyBinding:  (brokerName) => api.post('/broker/proxy-reservation', { broker_name: brokerName }),
  refreshKotakSession:  (totp)       => api.post('/broker/session/kotakneo/refresh', { totp }),
  // Auto-token (FYERS TOTP automation)
  setupAutoToken:       ()           => api.post('/broker/auto-token/setup'),
  disableAutoToken:     ()           => api.delete('/broker/auto-token/disable'),
  getAutoTokenStatus:   ()           => api.get('/broker/auto-token/status'),
};
